import { EnableHeadMouseMode } from '@shared/constants/FeatureFlagValues';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useRef } from 'react';
import { useDeviceStore } from './state/DeviceContainer';
import useStateRef from './useStateRef';

export default function useFeatureFlagEditor(initialFlags: string[]) {
    const [featureFlags, setFeatureFlags, featureFlagRef] = useStateRef<string[]>(initialFlags);
    const initialFlagsRef = useRef(initialFlags);

    const updateFeatureFlags = (flagName: string, isEnabled: boolean) => {
        setFeatureFlags((prevFlags) => {
            const hasFlag = prevFlags.includes(flagName);

            if (isEnabled && !hasFlag) {
                return [...prevFlags, flagName];
            } else if (!isEnabled && hasFlag) {
                return prevFlags.filter((flag) => flag !== flagName);
            }

            return prevFlags;
        });
    };

    const handleEnabledFeatureFlagChange = async () => {
        // TODO HEAD MOUSE: Add head mouse settings when the feature flag is enabled
        if (
            initialFlagsRef.current.includes(EnableHeadMouseMode) &&
            !featureFlagRef.current.includes(EnableHeadMouseMode)
        ) {
            for (const device of useDeviceStore.getState().devices) {
                if (
                    device.expressionSettings.mouseMoveMode == undefined ||
                    device.expressionSettings.mouseMoveMode === 'Off'
                )
                    continue;

                try {
                    await useDeviceStore.getState().updateDevice(device.id, {
                        ...device,
                        expressionSettings: {
                            ...device.expressionSettings, // Retain existing settings
                            mouseMoveLeftExpression: undefined,
                            mouseMoveRightExpression: undefined,
                            mouseMoveUpExpression: undefined,
                            mouseMoveDownExpression: undefined,
                            mouseMoveMode: 'Off',
                        },
                    });
                } catch (error) {
                    return new UnexpectedResult<string>();
                }
            }
        }
        return new SuccessResult<string>(EnableHeadMouseMode);
    };

    /**
     * Handles the extended feature flag change on user preference save
     * Use this when you need other functionality to be triggered when a feature flag is changed using if conditions
     * @returns A result indicating the success or failure of the operation
     */
    const handleExtendedFeatureFlagChange = async () => {
        const flagHandlers: { [key: string]: () => Promise<SuccessResult<string> | UnexpectedResult<string>> } = {
            [EnableHeadMouseMode]: handleEnabledFeatureFlagChange,
            // Add more feature flags and their handlers here
        };

        try {
            const results = await Promise.all(
                Object.entries(flagHandlers).map(([flag, handler]) => {
                    if (featureFlagRef.current.includes(flag) || initialFlagsRef.current.includes(flag)) {
                        return handler(); // Call the handler if the feature flag is has existed in initial or current flags
                    }
                    return Promise.resolve(new SuccessResult<string>('')); // Resolve with a SuccessResult if not enabled
                })
            );

            // Check for any UnexpectedResults and return the first one found
            const unexpectedResult = results.find((result) => result instanceof UnexpectedResult);
            if (unexpectedResult) {
                return unexpectedResult; // Return the UnexpectedResult if found
            }

            // Reset the initial flags to the current flags after handling since those have been saved.
            // This ensures the next 'save' click behaves as expected.
            initialFlagsRef.current = featureFlagRef.current;
            return new SuccessResult<string>(''); // Return a generic success result
        } catch (error) {
            return new UnexpectedResult();
        }
    };

    return { featureFlags, updateFeatureFlags, handleExtendedFeatureFlagChange };
}
